import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import LanguageSelector from "@/components/LanguageSelector";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Calendar,
  LayoutDashboard,
  Briefcase,
  Users,
  CalendarDays,
  BookOpen,
  Settings,
  LogOut,
  Menu,
  X,
  Building2,
  ExternalLink,
  Crown,
  Shield,
  Store,
  Globe
} from "lucide-react";
import { useState } from "react";

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Menu diversi in base al ruolo
  const isSuperAdmin = user?.role === "super_admin";
  const isAdmin = user?.role === "admin";
  const isBusinessOwner = user?.role === "business_owner";
  
  // Menu per Super Admin
  const superAdminNavigation = [
    { name: t('superadmin.title'), href: "/superadmin", icon: Crown },
    { name: t('dashboard.businesses'), href: "/superadmin?tab=businesses", icon: Building2 },
    { name: t('dashboard.settings'), href: "/superadmin?tab=settings", icon: Settings },
  ];

  // Menu per Admin
  const adminNavigation = [
    { name: t('admin.title'), href: "/admin", icon: Shield },
    { name: t('dashboard.businesses'), href: "/admin", icon: Building2 },
  ];

  // Menu per Business Owner
  const businessNavigation = [
    { name: "Dashboard", href: "/my-business", icon: Store },
    { name: t('dashboard.services'), href: "/my-business?tab=services", icon: Briefcase },
    { name: t('dashboard.staff'), href: "/my-business?tab=staff", icon: Users },
    { name: t('dashboard.bookings'), href: "/my-business?tab=bookings", icon: BookOpen },
    { name: t('dashboard.settings'), href: "/my-business?tab=settings", icon: Settings },
  ];

  const navigation = isSuperAdmin ? superAdminNavigation : 
                      isAdmin ? adminNavigation : 
                      businessNavigation;
  
  const dashboardPath = isSuperAdmin ? "/superadmin" : 
                        isAdmin ? "/admin" : 
                        "/my-business";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";
  };

  const getRoleBadge = () => {
    if (isSuperAdmin) return { text: "Super Admin", color: "bg-purple-100 text-purple-700 border-purple-200" };
    if (isAdmin) return { text: "Admin", color: "bg-blue-100 text-blue-700 border-blue-200" };
    if (isBusinessOwner) return { text: "Azienda", color: "bg-green-100 text-green-700 border-green-200" };
    return null;
  };

  const roleBadge = getRoleBadge();

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200">
            <Link to={dashboardPath} className="flex items-center gap-2">
              <Calendar className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                PrenotaFacile
              </span>
            </Link>
            <button
              className="lg:hidden text-slate-500 hover:text-slate-700"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Role Badge */}
          {roleBadge && (
            <div className={`px-4 py-3 border-b ${roleBadge.color}`}>
              <span className="text-xs font-semibold uppercase tracking-wide">
                {roleBadge.text}
              </span>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item, index) => {
              const isActive = location.pathname === item.href || 
                location.pathname.startsWith(item.href);
              
              // Skip duplicates for admin views (they share same base path)
              if ((isSuperAdmin || isAdmin || isBusinessOwner) && index > 0 && item.href === navigation[0].href) {
                // These are tab-based navigation, show them but they all go to same page
              }
              
              return (
                <Link
                  key={`${item.name}-${index}`}
                  to={item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive && index === 0
                      ? "bg-primary/10 text-primary"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="h-5 w-5" strokeWidth={1.5} />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-200">
            {/* Language Selector for Desktop */}
            <div className="mb-3 hidden lg:block">
              <LanguageSelector variant="outline" showLabel={true} />
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="user-menu-btn">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {user?.name}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {user?.email}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{t('auth.login.title', 'Il mio account')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isBusinessOwner && (
                  <>
                    <DropdownMenuItem asChild>
                      <Link to="/my-business" className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        {t('common.settings', 'Impostazioni')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('common.logout', 'Esci')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <header className="sticky top-0 z-30 flex items-center justify-between h-16 px-4 bg-white border-b border-slate-200 lg:hidden">
          <div className="flex items-center">
            <button
              className="text-slate-500 hover:text-slate-700"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center gap-2 ml-4">
              <Calendar className="h-6 w-6 text-primary" strokeWidth={1.5} />
              <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                PrenotaFacile
              </span>
            </div>
          </div>
          <LanguageSelector variant="ghost" showLabel={false} />
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
